/**
 * Turning a message's ordered `parts` array into ordered render segments.
 *
 * Split out of `../messageTimeline.ts`, which carries the full rationale. The
 * one-line version: `parts` is already chronological, and this walks it once,
 * in order, coalescing CONSECUTIVE tool invocations into a single run. Anything
 * that is said — text, reasoning, artifact, context — closes the run;
 * `step-start` does not.
 */

import type { ToolInvocation, ToolPartWithState } from '../toolInvocations.js';
import { isArtifactUIPart } from '../../../lib/artifacts/types.js';
import { parsePlanInput, UPDATE_PLAN_TOOL_NAME } from '../../../lib/plan/index.js';
import { stripInvisibleChars } from './text.js';
import {
  TOOL_TYPE_PREFIX,
  isRecord,
  isToolPartLike,
  optionalString,
  resolveOutstandingApprovalId,
  resolveToolName,
} from './tool-parts.js';
import type {
  MessageFilePart,
  MessageTimeline,
  PlanSegment,
  TimelineSegment,
  ToolsSegment,
} from './types.js';

export function buildMessageTimeline(parts: readonly unknown[]): MessageTimeline {
  const segments: TimelineSegment[] = [];
  const fileParts: MessageFilePart[] = [];
  /**
   * Every invocation seen so far, by tool-call id. A `tool-result` part is
   * normally adjacent to its call, but legacy rows can separate them — looking
   * back here attaches the result to the real call instead of emitting an
   * orphan row in a later run.
   */
  const invocationById = new Map<string, ToolInvocation>();
  let hasVisibleText = false;
  let planSegment: PlanSegment | null = null;
  let currentRun: ToolsSegment | null = null;
  let keyCounter = 0;
  /**
   * Ids for parts that arrive without a `toolCallId`. Each gets a UNIQUE id:
   * a shared positional fallback lets an unpaired result collide with the next
   * call and swallow it.
   */
  let anonToolIndex = 0;

  for (const raw of parts) {
    if (!isRecord(raw)) continue;
    const type = optionalString(raw.type) ?? '';

    // Transient step marker: not a timeline event, and must not split a run.
    if (type === 'step-start') continue;

    if (type === 'text') {
      const text = optionalString(raw.text) ?? '';
      // Skip whitespace/invisible-only text so a mid-stream empty text part
      // can't wedge a blank prose block between two tool runs.
      if (stripInvisibleChars(text).trim().length === 0) continue;
      hasVisibleText = true;
      currentRun = null;
      segments.push({ kind: 'text', key: `text-${keyCounter++}`, text });
      continue;
    }

    if (type === 'reasoning') {
      const text = optionalString(raw.text) ?? '';
      if (text.trim().length === 0) continue;
      currentRun = null;
      // Reasoning arrives in chunks; merge adjacent ones into one disclosure.
      const previous = segments[segments.length - 1];
      if (previous && previous.kind === 'reasoning') {
        previous.text = `${previous.text}\n\n${text}`;
      } else {
        segments.push({ kind: 'reasoning', key: `reasoning-${keyCounter++}`, text });
      }
      continue;
    }

    // Artifacts. The guard validates the payload, so a malformed/legacy part
    // falls through to "no card" instead of crashing the row.
    if (isArtifactUIPart(raw)) {
      currentRun = null;
      segments.push({ kind: 'artifact', key: `artifact-${raw.data.id}`, part: raw });
      continue;
    }

    if (type === 'context') {
      currentRun = null;
      segments.push({
        kind: 'context',
        key: `context-${keyCounter++}`,
        content: optionalString(raw.content),
        source: optionalString(raw.source),
      });
      continue;
    }

    if (type === 'file') {
      fileParts.push({
        filename: optionalString(raw.filename),
        url: optionalString(raw.url),
        size: typeof raw.size === 'number' ? raw.size : undefined,
        contentType: optionalString(raw.contentType),
        mediaType: optionalString(raw.mediaType),
      });
      continue;
    }

    if (!type.startsWith(TOOL_TYPE_PREFIX) || !isToolPartLike(raw)) continue;

    // Bind to the declared shape: the `Record<string, unknown>` narrowing above
    // widens every field to `unknown`, which loses `toolCallId: string`.
    const toolPart: ToolPartWithState = raw;
    const toolName = resolveToolName(toolPart);

    // `update_plan` renders as a checklist anchored at its first appearance;
    // later calls only refresh the payload. It never splits a tool run.
    if (toolName === UPDATE_PLAN_TOOL_NAME) {
      const plan = parsePlanInput(toolPart.input ?? toolPart.args);
      if (toolPart.toolCallId) {
        invocationById.set(toolPart.toolCallId, {
          toolCallId: toolPart.toolCallId,
          toolCall: toolPart,
        });
      }
      if (!plan) continue;
      if (planSegment) {
        planSegment.plan = plan;
      } else {
        planSegment = { kind: 'plan', key: `plan-${keyCounter++}`, plan };
        currentRun = null;
        segments.push(planSegment);
      }
      continue;
    }

    const isResultPart = type === 'tool-result';
    const id = toolPart.toolCallId ?? `tool-anon-${anonToolIndex++}`;
    const existing = invocationById.get(id);

    if (isResultPart) {
      if (existing) {
        existing.result = toolPart;
      } else {
        // Result with no known call: keep it visible as its own invocation.
        const invocation: ToolInvocation = { toolCallId: id, toolCall: {}, result: toolPart };
        invocationById.set(id, invocation);
        if (!currentRun) {
          currentRun = { kind: 'tools', key: `tools-${keyCounter++}`, invocations: [] };
          segments.push(currentRun);
        }
        currentRun.invocations.push(invocation);
      }
      continue;
    }

    if (existing) {
      // The same part mutates in place while streaming (input-streaming →
      // output-available); refresh the call and leave its position alone.
      existing.toolCall = toolPart;
      continue;
    }

    const invocation: ToolInvocation = { toolCallId: id, toolCall: toolPart };
    invocationById.set(id, invocation);

    const approvalId = resolveOutstandingApprovalId(toolPart);
    if (approvalId) {
      // Approvals need user action and must never hide inside a collapsed run.
      currentRun = null;
      segments.push({
        kind: 'approval',
        key: `approval-${id}`,
        invocation,
        approvalId,
        toolName: toolName || 'Tool',
      });
      continue;
    }

    if (!currentRun) {
      currentRun = { kind: 'tools', key: `tools-${keyCounter++}`, invocations: [] };
      segments.push(currentRun);
    }
    currentRun.invocations.push(invocation);
  }

  return { segments, fileParts, hasVisibleText, toolCount: invocationById.size };
}
